import { useState } from 'react';
import { clsx } from 'clsx';

import { toast } from '../../Toast';
import { Info } from '../../Info/Info';

import { FinancialInstrumentForm } from './FinancialInstrumentForm';
import { FinancialInstrumentConfirmModal } from './FinancialInstrumentConfirmModal';
import { Loading } from '../components/loading';
import { useFinancialInstrumentTranslation } from './i18n';
import {
  useFinancialInstrument,
  useCreateFinancialInstrument,
  useUpdateFinancialInstrument,
  useDeleteFinancialInstrument,
  useInstrumentTypes,
} from './hooks';
import type {
  CreateFinancialInstrumentPayload,
  FinancialInstrumentHttpError,
  FinancialInstrumentPanelProps,
} from './types';

/**
 * Panel de Instrumento Financiero (IF) — sub-componente de MasterDocument
 * (FEAT-105-17).
 *
 * Captura/edita/elimina el único IF activo (cheque, pagaré, letra) asociado a un
 * documento. Sin IF → modo crear (POST); con IF → modo editar pre-poblado (PUT
 * con `rowVersion`); Eliminar → soft-delete (libera el slot). Fuera de DRAFT o
 * sin `capture_financial_instrument` el formulario queda read-only. El campo tipo
 * es editable solo con `change_default_financial_instrument` (FR-84).
 *
 * Diseñado para montarse en una sección/FactBox de cualquier documento
 * MasterDocument inyectando endpoints, IO y catálogo por props (precedente:
 * InstallmentsPanel / WithholdingsPanel).
 *
 * @example
 * ```tsx
 * <FinancialInstrumentPanel
 *   documentId={docId}
 *   isEditable={docState === 'DRAFT'}
 *   canCapture={permissions.includes(CORE_PERMISSIONS.SUPPLIER_INVOICES.CAPTURE_FINANCIAL_INSTRUMENT)}
 *   canChangeDefaultType={permissions.includes(CORE_PERMISSIONS.SUPPLIER_INVOICES.CHANGE_DEFAULT_FINANCIAL_INSTRUMENT)}
 *   financialInstrumentEndpoint={`/api/v1/purchases/supplier-invoices/${docId}/financial-instrument`}
 *   financialInstrumentBaseEndpoint={`/api/v1/purchases/supplier-invoices/${docId}/financial-instrument`}
 *   httpRequest={httpRequest}
 *   catalogs={catalogs}
 * />
 * ```
 */
export function FinancialInstrumentPanel({
  documentId,
  isEditable,
  canCapture,
  canChangeDefaultType,
  financialInstrumentEndpoint,
  financialInstrumentBaseEndpoint,
  httpRequest,
  catalogs,
  defaultInstrumentTypeId,
  defaultInstrumentTypeName,
  initialInstrument,
  className,
}: FinancialInstrumentPanelProps) {
  const { t } = useFinancialInstrumentTranslation();

  const [confirmOpen, setConfirmOpen] = useState(false);

  const instrumentQuery = useFinancialInstrument({
    documentId,
    financialInstrumentEndpoint,
    httpRequest,
    initialData: initialInstrument,
  });
  const typesQuery = useInstrumentTypes({ catalogs, enabled: canChangeDefaultType });

  const createMutation = useCreateFinancialInstrument({
    documentId,
    financialInstrumentBaseEndpoint,
    httpRequest,
  });
  const updateMutation = useUpdateFinancialInstrument({
    documentId,
    financialInstrumentBaseEndpoint,
    httpRequest,
  });
  const deleteMutation = useDeleteFinancialInstrument({
    documentId,
    financialInstrumentBaseEndpoint,
    httpRequest,
  });

  const instrument = instrumentQuery.data ?? null;
  const canEdit = isEditable && canCapture;

  function reportError(error: unknown) {
    const err = error as FinancialInstrumentHttpError;
    const detail = err?.detail;
    switch (err?.status) {
      case 403:
        toast.error(t('financial_instrument.error.forbidden'));
        return;
      case 404:
        toast.error(detail || t('financial_instrument.error.notFound'));
        return;
      case 409:
        toast.error(detail || t('financial_instrument.error.concurrency'));
        return;
      case 422:
        toast.error(detail || t('financial_instrument.error.validation'));
        return;
      default:
        toast.error(detail || err?.message || t('financial_instrument.error.generic'));
    }
  }

  function handleSubmit(values: CreateFinancialInstrumentPayload) {
    if (instrument) {
      updateMutation.mutate(
        {
          financialInstrumentId: instrument.id,
          payload: { ...values, rowVersion: instrument.rowVersion },
        },
        {
          onSuccess: () => toast.success(t('financial_instrument.toast.updated')),
          onError: reportError,
        },
      );
    } else {
      createMutation.mutate(values, {
        onSuccess: () => toast.success(t('financial_instrument.toast.created')),
        onError: reportError,
      });
    }
  }

  function handleDelete() {
    if (!instrument) return;
    deleteMutation.mutate(instrument.id, {
      onSuccess: () => {
        toast.success(t('financial_instrument.toast.deleted'));
        setConfirmOpen(false);
      },
      onError: (error) => {
        reportError(error);
        setConfirmOpen(false);
      },
    });
  }

  return (
    <section
      className={clsx('flex flex-col gap-3', className)}
      aria-label={t('financial_instrument.panel.title')}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-content-primary dark:text-dark-content-primary truncate">
          {canEdit
            ? t('financial_instrument.panel.title')
            : t('financial_instrument.panel.readonly')}
        </h3>
      </div>

      {instrumentQuery.isError ? (
        <Info color="red" title={t('financial_instrument.panel.loadError')} />
      ) : instrumentQuery.isLoading ? (
        <Loading size="md" label={t('financial_instrument.panel.loading')} />
      ) : (
        <FinancialInstrumentForm
          instrument={instrument}
          canEdit={canEdit}
          canChangeDefaultType={canChangeDefaultType}
          instrumentTypes={typesQuery.data ?? []}
          typesLoading={typesQuery.isLoading}
          defaultInstrumentTypeId={defaultInstrumentTypeId}
          defaultInstrumentTypeName={defaultInstrumentTypeName}
          isSaving={createMutation.isPending || updateMutation.isPending}
          isDeleting={deleteMutation.isPending}
          onSubmit={handleSubmit}
          onDelete={() => setConfirmOpen(true)}
        />
      )}

      <FinancialInstrumentConfirmModal
        open={confirmOpen}
        title={t('financial_instrument.delete.title')}
        body={t('financial_instrument.delete.body')}
        confirmLabel={t('financial_instrument.delete.confirm')}
        cancelLabel={t('financial_instrument.delete.cancel')}
        isLoading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </section>
  );
}
